import clsx from "clsx";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { dash, money, percentDelta, points } from "../lib/format";
import { PlayerAvatar } from "./PlayerAvatar";
import type { Position } from "../lib/positions";
import { PositionChip, StatusChip, type Status } from "./ui/Chip";

/**
 * La tarjeta de jugador y su gemela de escritorio, la fila de tabla (secciones 8.1 y 8.4).
 *
 * QUÉ FORMA TIENEN LOS DATOS
 *   `PlayerCardData` es un recorte **estructural** de `PlayerWithMetrics` (sección 9.2): pide lo
 *   que pinta y nada más. Cuando M7-T02 traiga los tipos del contrato, encajarán sin conversión
 *   y sin que esta tarjeta dependa de campos que no usa.
 *
 * LA REGLA DE LOS 768 PX
 *   La sección 8.1 pide que en pantalla ancha la lista de tarjetas sea una tabla. `PlayerList`
 *   monta las dos y deja que el CSS elija: sin medir el ancho con JavaScript no hay parpadeo en
 *   la primera pintura ni un estado más que mantener. La tabla va dentro de un contenedor con
 *   scroll propio, de modo que si una columna crece, se desplaza ella y no la página.
 *
 * NINGÚN ESTADO SE DISTINGUE SOLO POR COLOR
 *   Quien está en el plan lleva borde de acento **y** una franja vertical; quien ha expirado va
 *   atenuado **y** con el texto que lo dice en la columna de la pantalla.
 */

export interface PlayerCardData {
  id: number;
  name: string;
  position: Position;
  team: { name: string; shortName: string } | null;
  price: number;
  /** Variación del precio en euros (sección 9.2). */
  priceIncrement?: number;
  status?: Status;
  statusText?: string | null;
  metrics?: {
    expectedPpr: number;
    rankPosition: number;
    trend7d: number;
  } | null;
}

export type PlayerState = "default" | "inPlan" | "expired";

/** Columna propia de cada pantalla: ganancia en Plantilla, vendedor y plazo en Mercado. */
export interface PlayerColumn {
  header: string;
  render: (player: PlayerCardData) => ReactNode;
}

function rankLabel(player: PlayerCardData): string | null {
  const rango = player.metrics?.rankPosition;
  if (rango === undefined || rango === null || rango <= 0) return null;
  return `#${rango} ${player.position}`;
}

function trendNode(player: PlayerCardData, className?: string): ReactNode {
  const tendencia = player.metrics?.trend7d;
  if (tendencia === undefined || tendencia === null) {
    return <span className={clsx("text-muted", className)}>—</span>;
  }
  return (
    <span
      className={clsx(
        "font-mono tabular-nums",
        tendencia < 0 ? "text-down" : "text-up",
        className,
      )}
    >
      {percentDelta(tendencia)}
    </span>
  );
}

export interface PlayerCardProps {
  player: PlayerCardData;
  state?: PlayerState;
  /** Botones de plan; se apilan bajo el nombre para no competir con el enlace a la ficha. */
  actions?: ReactNode;
  /** Dato propio de la pantalla, en la línea de metadatos. */
  extra?: ReactNode;
  /** Puntos esperados a mostrar; por defecto los de las métricas. */
  expected?: number | null;
  className?: string;
}

export function PlayerCard({
  player,
  state = "default",
  actions,
  extra,
  expected,
  className,
}: PlayerCardProps) {
  const rango = rankLabel(player);
  const puntos = expected ?? player.metrics?.expectedPpr ?? null;

  return (
    <article
      className={clsx(
        "relative grid grid-cols-[auto_1fr_auto] items-center gap-2.5 rounded-xl border bg-surface px-3 py-2.5",
        state === "inPlan" ? "border-pitch" : "border-border",
        state === "expired" && "opacity-45",
        className,
      )}
    >
      {state === "inPlan" ? (
        <span
          aria-hidden="true"
          className="absolute inset-y-0 left-0 w-1 rounded-l-xl bg-pitch"
        />
      ) : null}

      <PlayerAvatar playerId={player.id} name={player.name} />

      <div className="min-w-0">
        {/* El enlace estira su área de pulsación a toda la tarjeta con un pseudoelemento: el
            nombre solo mide 20 px de alto y la sección 8.1 pide 44. Así se toca la tarjeta
            entera sin duplicar el enlace ni envolverla en otro. */}
        <Link
          to={`/jugador/${player.id}`}
          className="block truncate font-semibold text-ink no-underline after:absolute after:inset-0 after:content-['']"
        >
          {player.name}
        </Link>
        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted">
          <PositionChip position={player.position} />
          {player.team ? (
            <span className="truncate">{player.team.shortName}</span>
          ) : null}
          {player.status ? (
            <StatusChip status={player.status} detail={player.statusText} />
          ) : null}
          {rango ? (
            <span className="font-mono tabular-nums">{rango}</span>
          ) : null}
          {extra}
        </div>
        {actions ? (
          // Por encima del área estirada del enlace: si no, los botones dejarían de pulsarse.
          <div className="relative z-10 mt-2 flex flex-wrap gap-2">
            {actions}
          </div>
        ) : null}
      </div>

      <div className="grid justify-items-end gap-0.5 text-right">
        <span className="font-display text-[22px] font-semibold leading-none tabular-nums">
          {dash(puntos, points)}
        </span>
        <span className="font-mono text-xs text-money">
          {money(player.price)}
        </span>
        {trendNode(player, "text-[11px]")}
      </div>
    </article>
  );
}

export interface PlayerListProps {
  players: PlayerCardData[];
  column?: PlayerColumn;
  actions?: (player: PlayerCardData) => ReactNode;
  extra?: (player: PlayerCardData) => ReactNode;
  state?: (player: PlayerCardData) => PlayerState;
  expected?: (player: PlayerCardData) => number | null;
  /** Nombre de la lista, para el lector de pantalla y el `aria-label` de la tabla. */
  label: string;
  /**
   * Datos anteriores mientras llegan los nuevos: opacidad reducida (sección 8.2). La lista no se
   * vacía al cambiar un filtro, se atenúa.
   */
  stale?: boolean;
  className?: string;
}

const TH =
  "px-2.5 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.09em] text-muted";
const TH_NUM = `${TH.replace("text-left", "text-right")}`;
const TD = "px-2.5 py-2.5 text-[13px]";
/** Las cifras van a la derecha y con ancho de dígito fijo: es lo que hace legible una tabla. */
const TD_NUM = `${TD} text-right font-mono tabular-nums`;

/** La misma lista en tarjetas hasta 768 px y en tabla a partir de ahí (sección 8.1). */
export function PlayerList({
  players,
  column,
  actions,
  extra,
  state,
  expected,
  label,
  stale = false,
  className,
}: PlayerListProps) {
  const atenuado = stale ? "opacity-60" : undefined;

  return (
    <div className={clsx(atenuado, "transition-opacity", className)}>
      <ul aria-label={label} className="grid gap-2.5 md:hidden">
        {players.map((player) => (
          <li key={player.id}>
            <PlayerCard
              player={player}
              state={state?.(player)}
              actions={actions?.(player)}
              extra={extra?.(player)}
              expected={expected?.(player)}
            />
          </li>
        ))}
      </ul>

      <div className="hidden overflow-x-auto md:block">
        <table
          aria-label={label}
          className="w-full border-collapse overflow-hidden rounded-xl border border-border bg-surface"
        >
          <thead>
            <tr className="border-b border-border">
              <th scope="col" className={TH}>
                Jugador
              </th>
              <th scope="col" className={TH}>
                Pos
              </th>
              <th scope="col" className={TH}>
                Equipo
              </th>
              <th scope="col" className={TH}>
                Estado
              </th>
              <th scope="col" className={TH_NUM}>
                Pts/jorn.
              </th>
              <th scope="col" className={TH_NUM}>
                Rango
              </th>
              <th scope="col" className={TH_NUM}>
                Precio
              </th>
              <th scope="col" className={TH_NUM}>
                Tend. 7d
              </th>
              {column ? (
                <th scope="col" className={TH_NUM}>
                  {column.header}
                </th>
              ) : null}
              {actions ? <th scope="col" className={TH} /> : null}
            </tr>
          </thead>
          <tbody>
            {players.map((player) => {
              const fila = state?.(player) ?? "default";
              const puntos =
                expected?.(player) ?? player.metrics?.expectedPpr ?? null;

              return (
                <tr
                  key={player.id}
                  className={clsx(
                    "border-b border-border last:border-b-0",
                    fila === "inPlan" &&
                      "border-l-4 border-l-pitch bg-pitch/[0.08]",
                    fila === "expired" && "opacity-45",
                  )}
                >
                  <td className={TD}>
                    <Link
                      to={`/jugador/${player.id}`}
                      className="text-ink no-underline"
                    >
                      {player.name}
                    </Link>
                  </td>
                  <td className={TD}>
                    <PositionChip position={player.position} />
                  </td>
                  <td className={TD}>{player.team?.shortName ?? "—"}</td>
                  <td className={TD}>
                    {player.status ? (
                      <StatusChip
                        status={player.status}
                        detail={player.statusText}
                      />
                    ) : null}
                  </td>
                  <td className={TD_NUM}>{dash(puntos, points)}</td>
                  <td className={TD_NUM}>
                    {rankLabel(player)?.split(" ")[0] ?? "—"}
                  </td>
                  <td className={clsx(TD_NUM, "text-money")}>
                    {money(player.price)}
                  </td>
                  <td className={clsx(TD, "text-right")}>
                    {trendNode(player)}
                  </td>
                  {column ? (
                    <td className={TD_NUM}>{column.render(player)}</td>
                  ) : null}
                  {actions ? <td className={TD}>{actions(player)}</td> : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
